import ShoppingCartIcon from '@components/ShoppingCartIcon';
import FreshCheckAddress from '@features/Fresh/FreshCheckAddress';
import { useSideCart } from '@hooks/cart/useShoppingCart';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useRef } from 'react';

const disableHoverForRoutes = [
  '/cart',
  '/account/register/flow',
  '/checkout/delivery',
  '/checkout/payment',
];

const CartContent = dynamic(async () => {
  return await import('./CartContent');
});

const SideShoppingCart: React.FC = () => {
  const router = useRouter();
  const isButtonDisabled =
    disableHoverForRoutes.includes(router?.pathname) ||
    disableHoverForRoutes.includes(router?.asPath);
  const { sideCart, dispatchSideCart } = useSideCart();
  const refContainer = useRef<HTMLDivElement>(null);
  const { isOpen, shouldAddressBeVerified } = sideCart;
  const tCart = useTranslations('Cart');

  return (
    <>
      <Link
        href="/cart"
        className="tw-flex desktop:tw-sr-only"
        aria-label={tCart('myCart')}
        data-testid="SideShoppingCart-Button-display-sidecart-mobile"
      >
        <ShoppingCartIcon />
      </Link>
      <button
        onClick={() => dispatchSideCart({ type: 'viewMyCart' })}
        disabled={isButtonDisabled}
        className="tw-lf-button-clear tw-hidden desktop:tw-flex"
        aria-hidden
        data-testid="SideShoppingCart-Button-display-sidecart"
      >
        <ShoppingCartIcon />
      </button>
      {isOpen && (
        <div
          className="overlay"
          onClick={() => dispatchSideCart({ type: 'close' })}
        />
      )}
      <div className="container" ref={refContainer}>
        {isOpen ? (
          shouldAddressBeVerified ? (
            <FreshCheckAddress />
          ) : (
            <div className="cart-container">
              <CartContent />
            </div>
          )
        ) : null}
      </div>
      <style jsx>{`
        .overlay {
          background-color: var(--clr-overlay);
          box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
          position: fixed;
          top: 0;
          right: 0;
          width: 100%;
          height: 100%;
        }

        .cart-container {
          display: flex;
          flex-direction: row;
          height: 100%;
        }

        .container {
          transform: ${isOpen ? `translateX(0)` : `translateX(100%)`};
          visibility: ${isOpen ? `visible` : `hidden`};
          transition: all 0.3s ease-out;
          display: flex;
          flex-direction: column;
          position: fixed;
          bottom: 0;
          right: 0;
          height: 100%;
          z-index: var(--zindex-fixed);
        }

        :global(#yuma-widget-container iframe) {
          ${isOpen ? 'display: none !important;' : ''}
        }

        :global([id^='cobbai-']) {
          ${isOpen ? 'display: none !important;' : ''}
        }

        .max-weight-error {
          padding: var(--space-4) var(--space-4) 0 var(--space-4);
          text-align: center;
        }

        .content :global(.oos-products) {
          margin: var(--space-4);
        }

        .content :global(.oos-product-content) {
          width: 100%;
        }

        .type-quantity {
          font-weight: var(--font-weight-black);
          font-family: var(--font-family-1);
        }

        @keyframes bounce_right {
          0% {
            transform: translateX(0);
          }

          10% {
            transform: translateX(75%);
          }
        }
      `}</style>
    </>
  );
};

export default SideShoppingCart;
